import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import {
  type SubmitHandler,
  useForm,
} from 'react-hook-form'
import { TailSpin } from 'react-loading-icons'
import { toast } from 'sonner'

import AssigneesFilter from 'components/brokers/needs_attention/AssigneesFilter'
import { Button } from 'components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog'
import { Label } from 'components/ui/label'
import { usePatchBrokersTasks } from 'utils/web_api'

export type BulkUpdateTasksDialogProps = {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  selectedItems: string[]
}

type BulkUpdateTasksFormInput = {
  broker_ids_to_add: string[]
  broker_ids_to_remove: string[]
}

const BulkUpdateTasksDialog = ({
  isOpen,
  setIsOpen,
  selectedItems,
}: BulkUpdateTasksDialogProps) => {
  const queryClient = useQueryClient()

  const [assigneesValidationError, setAssigneesValidationError] = useState<string | null>(null)

  const { handleSubmit, setValue, watch, reset } = useForm<BulkUpdateTasksFormInput>({
    defaultValues: {
      broker_ids_to_add: [],
      broker_ids_to_remove: [],
    },
  })

  const toggleState = (
    key: 'broker_ids_to_add' | 'broker_ids_to_remove',
    value: string
  ) => {
    const currentValue = watch(key)
    if (currentValue.includes(value)) {
      setValue(key, currentValue.filter((v) => v !== value))
    } else {
      setValue(key, [...currentValue, value])
    }
  }

  const assigneesToAdd = watch('broker_ids_to_add')
  const assigneesToRemove = watch('broker_ids_to_remove')

  const bulkUpdateMutation = usePatchBrokersTasks()

  const handleOnOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (!open) {
      reset()
      setAssigneesValidationError(null)
    }
  }

  useEffect(() => {
    const conflict = assigneesToAdd.find((assignee) =>
      assigneesToRemove.includes(assignee)
    )

    if (conflict) {
      setAssigneesValidationError(
        'Assignees cannot be both added and removed. Please remove duplicate assignees.'
      )
    } else {
      setAssigneesValidationError(null)
    }
  }, [assigneesToAdd, assigneesToRemove])

  const handleCancel = () => {
    setIsOpen(false)
  }

  const onSubmit: SubmitHandler<BulkUpdateTasksFormInput> = async (data) => {
    // Validate at least one action is specified
    const hasAssignees = data.broker_ids_to_add.length > 0 || data.broker_ids_to_remove.length > 0

    if (!hasAssignees) {
      toast.error('Please select assignees to add or remove')
      return
    }

    bulkUpdateMutation.mutate({
      data: {
        task_ids: selectedItems,
        ...(data.broker_ids_to_add.length > 0 && { broker_ids_to_add: data.broker_ids_to_add }),
        ...(data.broker_ids_to_remove.length > 0 && { broker_ids_to_remove: data.broker_ids_to_remove }),
      },
    }, {
      onSuccess: () => {
        // Invalidate all tasks queries to refetch with updated data
        queryClient.invalidateQueries({ queryKey: ['brokers', 'tasks'] })

        toast.success(`${selectedItems.length} tasks successfully updated!`, {
          position: 'top-center',
        })
        reset()
        setIsOpen(false)
      },
      onError: (error) => {
        console.error('Bulk update API error:', error)
        toast.error('Failed to update tasks. Please try again.', {
          position: 'top-center',
        })
      },
    }
    )
  }

  const isValidRequest = (
    (assigneesToAdd.length > 0 || assigneesToRemove.length > 0) &&
    !assigneesValidationError
  )

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Dialog open={isOpen} onOpenChange={handleOnOpenChange}>
        <DialogContent className='flex flex-col max-h-[90vh] p-0'>
          <DialogHeader className='p-6'>
            <DialogTitle className='text-xl'>
              Update Selected Tasks
            </DialogTitle>
            <DialogDescription>
              {selectedItems.length} task{selectedItems.length === 1 ? '' : 's'} selected
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-4 flex-1 overflow-y-auto p-6'>
            <div className='space-y-4'>
              {/* Assignee Management Section */}
              <div className='space-y-4'>
                <div className='space-y-2'>
                  <Label>Add Assignees</Label>
                  <AssigneesFilter
                    selectedValues={assigneesToAdd}
                    onSelectValue={(brokerId) => toggleState('broker_ids_to_add', brokerId)}
                  />
                </div>

                <div className='space-y-2'>
                  <Label>Remove Assignees</Label>
                  <AssigneesFilter
                    selectedValues={assigneesToRemove}
                    onSelectValue={(brokerId) => toggleState('broker_ids_to_remove', brokerId)}
                  />
                </div>

                {assigneesValidationError && (
                  <div className='text-red-600 text-sm'>
                    {assigneesValidationError}
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className='flex items-center justify-end mt-4 p-6'>
            <Button
              type='button'
              onClick={handleCancel}
              variant='secondary'
            >
              Cancel
            </Button>
            <Button
              disabled={selectedItems.length === 0 || !isValidRequest || bulkUpdateMutation.isPending}
              onClick={() => handleSubmit(onSubmit)()}
            >
              {bulkUpdateMutation.isPending
                ? (
                  <div className='flex items-center h-[100%]'>
                    <span>Updating...</span>
                    <TailSpin fill='#FFF' stroke='#FFF' className='h-[90%]' />
                  </div>
                  )
                : (
                    'Update'
                  )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form>
  )
}

export default BulkUpdateTasksDialog
